import React, { useState, useEffect } from 'react';
import { APPLE_THEME, TYPOGRAPHY } from '../theme';

const useIntersectionObserver = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold }
    );

    if (ref) {
      observer.observe(ref);
    }

    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [ref, threshold]);

  return [setRef, isVisible];
};

const APPS = [
  { name: 'Settings', icon: '⚙️', accentColor: '#007AFF' },
  { name: 'Messages', icon: '💬', accentColor: '#00D313' },
  { name: 'Phone', icon: '📞', accentColor: '#30D158' },
  { name: 'Mail', icon: '✉️', accentColor: '#007AFF' },

  { name: 'Safari', icon: '🧭', accentColor: '#007AFF' },
  { name: 'Music', icon: '🎵', accentColor: '#FF2D92' },
  { name: 'Maps', icon: '🗺️', accentColor: '#30D158' },
  { name: 'Photos', icon: '🖼️', accentColor: '#FF9F0A' },

  { name: 'Camera', icon: '📷', accentColor: '#8E8E93' },
  { name: 'Clock', icon: '⏰', accentColor: '#FF9F0A' },
  { name: 'Weather', icon: '🌤️', accentColor: '#007AFF' },
  { name: 'Calculator', icon: '🧮', accentColor: '#FF9F0A' },

  { name: 'Notes', icon: '📝', accentColor: '#FFD60A' },
  { name: 'Reminders', icon: '📋', accentColor: '#FF3B30' },
  { name: 'App Store', icon: 'A', accentColor: '#007AFF', isSpecial: true },
  { name: 'Health', icon: '❤️', accentColor: '#FF3B30' },

  { name: 'Wallet', icon: '💳', accentColor: '#1C1C1E' },
  { name: 'Files', icon: '📁', accentColor: '#007AFF' },
  { name: 'Contacts', icon: '👥', accentColor: '#8E8E93' },
  { name: 'Calendar', icon: '31', accentColor: '#FF3B30', isSpecial: true }
];

const DOCK_APPS = [
  { name: 'Phone', icon: '📞', accentColor: '#30D158' },
  { name: 'Safari', icon: '🧭', accentColor: '#007AFF' },
  { name: 'Mail', icon: '✉️', accentColor: '#007AFF' },
  { name: 'Music', icon: '🎵', accentColor: '#FF2D92' }
];

const IPhone3D = ({ isVisible }) => {
  const [rotation, setRotation] = useState({ x: -3, y: 5 });
  const [currentTime, setCurrentTime] = useState('');
  const [calMonth, setCalMonth] = useState('');
  const [calDay, setCalDay] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));
      const monthShort = now.toLocaleDateString('tr-TR', { month: 'short' });
      setCalMonth(monthShort.toLocaleUpperCase('tr-TR'));
      setCalDay(String(now.getDate()));
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);

    const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const handleMouseMove = (e) => {
      if (prefersReduced) return;
      if (window.innerWidth > 1024) {
        const x = (window.innerHeight / 2 - e.clientY) / 40;
        const y = (window.innerWidth / 2 - e.clientX) / 40;
        setRotation({
          x: Math.max(-15, Math.min(8, x - 3)),
          y: Math.max(-8, Math.min(20, y + 5))
        });
      }
    };

    const handleScroll = () => {
      if (prefersReduced) return;
      const scrollY = window.scrollY;
      const scrollRotationX = Math.sin(scrollY * 0.01) * 3;
      const scrollRotationY = Math.cos(scrollY * 0.01) * 2;

      if (window.innerWidth > 1024) {
        setRotation(prev => ({
          x: prev.x + scrollRotationX * 0.3,
          y: prev.y + scrollRotationY * 0.2
        }));
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timer);
    };
  }, []);

  const AppIcon = ({ app, size = 'normal' }) => {
    const sizeClasses = size === 'dock'
      ? 'w-12 h-12 sm:w-14 sm:h-14 rounded-xl'
      : 'w-11 h-11 sm:w-12 sm:h-12 lg:w-13 lg:h-13 rounded-2xl';

    return (
      <div
        className={`${sizeClasses} relative overflow-hidden flex items-center justify-center`}
        style={{
          backdropFilter: 'blur(25px)',
          WebkitBackdropFilter: 'blur(25px)',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          border: '0.5px solid rgba(255, 255, 255, 0.18)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)'
        }}
      >
        <div className="relative z-10">
          {app.isSpecial ? (
            app.name === 'App Store' ? (
              <div
                className="text-white text-lg font-bold"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
              >
                A
              </div>
    ) : app.name === 'Calendar' ? (
              <div className="flex flex-col items-center justify-center text-white text-center">
                <div
                  className="text-xs font-medium opacity-80 leading-none"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                >
      {calMonth}
                </div>
                <div
                  className="text-lg font-bold leading-none -mt-0.5"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                >
      {calDay}
                </div>
              </div>
            ) : (
              <div
                className="text-white text-base"
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))'
                }}
              >
                {app.icon}
              </div>
            )
          ) : (
            <div
              className="text-white text-base"
              style={{
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))'
              }}
            >
              {app.icon}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`transform-gpu transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-98'
        }`}
      style={{
        transform: window.innerWidth > 1024
          ? `perspective(1200px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) translateZ(40px)`
          : 'perspective(1200px) rotateX(-2deg) rotateY(3deg) translateZ(20px)',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.15s ease-out'
      }}
    >
      <div className="relative w-52 h-[420px] sm:w-60 sm:h-[480px] lg:w-64 lg:h-[520px]">
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 w-52 h-[210px] sm:w-60 sm:h-[240px] lg:w-64 lg:h-[260px] rounded-[40px] sm:rounded-[44px] lg:rounded-[48px] overflow-hidden opacity-20 z-0"
          style={{
            background: `linear-gradient(180deg, 
              rgba(58, 58, 60, 0.6) 0%,
              rgba(44, 44, 46, 0.4) 30%,
              rgba(26, 26, 28, 0.2) 60%,
              transparent 100%
            )`,
            transform: 'translateX(-50%) scaleY(-1) translateY(20px)',
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 80%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 80%)',
            filter: 'blur(1px)'
          }}
        />

        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 w-48 sm:w-56 lg:w-60 h-8 sm:h-10 lg:h-12 rounded-full opacity-15 blur-sm z-0"
          style={{
            background: 'radial-gradient(ellipse, rgba(0,0,0,0.4) 0%, transparent 70%)',
            transform: 'translateX(-50%) translateY(10px)'
          }}
        />

        
        <div
          className="absolute inset-0 rounded-[40px] sm:rounded-[44px] lg:rounded-[48px]"
          style={{
            background: `linear-gradient(145deg, #3a3a3c 0%, #2c2c2e 50%, #1a1a1c 100%)`,
            boxShadow: `
              0 30px 60px rgba(0,0,0,0.35),
              0 16px 32px rgba(0,0,0,0.25),
              0 8px 16px rgba(0,0,0,0.15),
              0 2px 4px rgba(0,0,0,0.1),
              inset 0 1px 0 rgba(255,255,255,0.1)
            `,
            transform: 'translateZ(20px)'
          }}
        >
          <div
            className="absolute inset-0 rounded-[40px] sm:rounded-[44px] lg:rounded-[48px] pointer-events-none"
            style={{
              background: 'linear-gradient(120deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.0) 30%, rgba(255,255,255,0.05) 60%, rgba(255,255,255,0.0) 100%)',
              mixBlendMode: 'screen'
            }}
          />
          <div
            className="absolute inset-[2.5px] sm:inset-[3px] rounded-[37px] sm:rounded-[41px] lg:rounded-[45px] bg-black overflow-hidden"
            style={{
              boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.08)'
            }}
          >
            <div
              className="absolute top-2 left-1/2 transform -translate-x-1/2 w-24 sm:w-28 h-6 sm:h-7 rounded-full z-30"
              style={{
                backgroundColor: '#000',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.8), 0 0 0 0.5px rgba(255,255,255,0.03)'
              }}
            />

            <div className="absolute top-0 left-0 right-0 h-12 sm:h-14 flex justify-between items-center px-5 sm:px-6 lg:px-8 text-white text-xs sm:text-sm font-medium z-20">
              <div className="font-semibold">{currentTime}</div>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[4, 3, 2, 1].map((strength) => (
                    <div
                      key={strength}
                      className="w-1 bg-white rounded-sm"
                      style={{
                        height: `${strength * 3}px`,
                        opacity: strength > 1 ? 1 : 0.4
                      }}
                    />
                  ))}
                </div>
                <svg className="w-4 h-3" viewBox="0 0 24 24" fill="white">
                  <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
                </svg>
                <div className="flex items-center">
                  <div className="w-6 h-3 border border-white rounded-sm relative">
                    <div className="absolute inset-0.5 bg-white rounded-sm" />
                  </div>
                  <div className="w-1 h-2 bg-white rounded-r-sm ml-0.5" />
                </div>
              </div>
            </div>

            <div
              className="absolute inset-0 rounded-[37px] sm:rounded-[41px] lg:rounded-[45px]"
              style={{
                background: `
                  radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                  radial-gradient(circle at 80% 80%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
                  radial-gradient(circle at 40% 40%, rgba(120, 200, 255, 0.2) 0%, transparent 50%),
                  linear-gradient(135deg, 
                    rgba(74, 144, 226, 0.8) 0%,
                    rgba(143, 148, 251, 0.8) 25%,
                    rgba(167, 112, 239, 0.8) 50%,
                    rgba(207, 159, 255, 0.8) 75%,
                    rgba(79, 172, 254, 0.8) 100%
                  )`
              }}
            />

            <div className="absolute top-16 sm:top-20 left-0 right-0 px-4 sm:px-5 lg:px-6 z-10">
              <div className="grid grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4">
                {APPS.map((app, index) => (
                  <AppIcon key={index} app={app} />
                ))}
              </div>
            </div>

            <div className="absolute bottom-4 left-0 right-0 px-6 z-10">
              <div
                className="mx-auto w-full max-w-[360px] sm:max-w-[300px] rounded-3xl py-2.5 px-4 flex gap-2 items-center justify-around"
                style={{
                  backdropFilter: 'blur(25px)',
                  WebkitBackdropFilter: 'blur(25px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '0.5px solid rgba(255, 255, 255, 0.18)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)'
                }}
              >
                {DOCK_APPS.map((app, i) => (
                  <AppIcon key={i} app={app} size="dock" />
                ))}
              </div>
            </div>


          </div>
        </div>

        <div className="absolute left-0 top-16 sm:top-20 w-[2px] h-8 sm:h-10 rounded-r-sm bg-gradient-to-b from-gray-600 to-gray-700" />
        <div className="absolute left-0 top-28 sm:top-32 w-[2px] h-12 sm:h-14 rounded-r-sm bg-gradient-to-b from-gray-600 to-gray-700" />
        <div className="absolute left-0 top-44 sm:top-48 w-[2px] h-6 sm:h-8 rounded-r-sm bg-gradient-to-b from-gray-600 to-gray-700" />
        <div className="absolute right-0 top-24 sm:top-28 w-[2px] h-16 sm:h-18 rounded-l-sm bg-gradient-to-b from-gray-600 to-gray-700" />

        <div
          className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 w-12 sm:w-14 h-1 rounded-full"
          style={{
            backgroundColor: '#1a1a1c',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8)'
          }}
        />
      </div>
    </div>
  );
};

const Hero = () => {
  const [ref, isVisible] = useIntersectionObserver(0.2);

  return (
    <section
      ref={ref}
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: APPLE_THEME.white }}
    >
      <div
        className="absolute inset-0 opacity-5"
        style={{
          background: `radial-gradient(ellipse at center, ${APPLE_THEME.lightMetalicGray} 0%, transparent 70%)`
        }}
      />

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-16 items-center min-h-screen lg:min-h-0 py-8 lg:py-16">

          <div className="relative flex justify-center items-center w-full h-[450px] sm:h-[500px] lg:h-[600px] order-1 lg:order-2">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
              <IPhone3D isVisible={isVisible} />
            </div>

            <div
              className="absolute inset-0 pointer-events-none hidden lg:block opacity-60"
              style={{
                background: `
                  radial-gradient(ellipse 300px 150px at 60% 40%, rgba(59, 130, 246, 0.1) 0%, transparent 60%),
                  radial-gradient(ellipse 250px 125px at 40% 60%, rgba(139, 92, 246, 0.08) 0%, transparent 60%)
                `
              }}
            />
          </div>

          <div
            className={`text-center lg:text-left transition-all duration-1000 order-2 lg:order-1 px-2 sm:px-0 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
      <p
              className="text-base sm:text-lg mb-3 sm:mb-4 tracking-wide"
              style={{
        color: APPLE_THEME.textGray,
                fontFamily: TYPOGRAPHY.body
              }}
            >
              Uzman Teknisyen Ekibimiz
            </p>

            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-[1.1] tracking-tight"
              style={{
                color: APPLE_THEME.black,
                fontFamily: TYPOGRAPHY.hero
              }}
            >
              Telefonunuz
              <br />
              <span style={{ color: APPLE_THEME.metalicGray }}>güvenli ellerde.</span>
            </h1>

      <p
              className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 sm:mb-10 lg:mb-12 leading-relaxed max-w-2xl mx-auto lg:mx-0"
              style={{
        color: APPLE_THEME.textGray,
                fontFamily: TYPOGRAPHY.body
              }}
            >
              Bursa'da 10 yıldır hizmet veriyoruz. Her marka telefonu orijinal kalitesinde
              tamir ediyoruz. Garantili işçilik, şeffaf fiyatlandırma.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                className="px-8 py-4 lg:px-10 lg:py-5 rounded-full text-lg lg:text-xl font-medium transition-all duration-200 hover:opacity-95 hover:scale-105 active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${APPLE_THEME.metalicGray} 0%, ${APPLE_THEME.darkMetalicGray} 100%)`,
                  color: APPLE_THEME.white,
                  fontFamily: TYPOGRAPHY.body,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}
                onClick={() => window.location.assign('/iletisim')}
              >
                Hemen İletişime Geç
              </button>
              <button
                className="px-8 py-4 lg:px-10 lg:py-5 rounded-full text-lg lg:text-xl font-medium border-2 transition-all duration-200 hover:bg-gray-50 hover:scale-105 active:scale-95"
                style={{
                  borderColor: APPLE_THEME.metalicGray,
                  color: APPLE_THEME.metalicBlack,
                  fontFamily: TYPOGRAPHY.body,
                  background: 'transparent'
                }}
                onClick={() => window.location.assign('/hizmetler')}
              >
                Hizmetlerimiz →
              </button>
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        aria-label="Aşağı kaydır"
        onClick={() => window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })}
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center justify-center w-10 h-10 rounded-full bg-black/5 text-black/60 hover:bg-black/10 transition animate-bounce"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </section>
  );
};

export default Hero;